import { getUrl } from 'aws-amplify/storage';
import log from 'loglevel';

export const sanitizeFilename = (
  name: string,
  extension: string = 'csv'
): string =>
  name
    .replace(/[^a-z0-9]/gi, '_')
    .slice(0, 175)
    .concat(`.${extension}`);

export const downloadFileFromS3 = async (
  name: string,
  s3Key: string,
  outputFormat: string = 'csv'
): Promise<void> => {
  if (!s3Key) {
    throw new Error('No S3 key provided for download');
  }

  const sanitizedFilename = sanitizeFilename(name, outputFormat);

  const { url } = await getUrl({
    path: `public/${s3Key}`,
    options: {
      contentDisposition: `attachment;filename=${sanitizedFilename}`,
    },
  });

  const response = await fetch(url.toString());

  if (!response.ok) {
    throw new Error('Access denied to the export file.');
  }

  const anchor = document.createElement('a');
  anchor.href = url.toString();
  anchor.download = sanitizedFilename;
  anchor.target = '_self';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
};

/**
 * Progress callback function type for download operations
 */
export type DownloadProgressCallback = (
  loaded: number,
  total: number,
  percentage: number
) => void;

/**
 * Options for file manager downloads
 */
export interface FileManagerDownloadOptions {
  /** Callback for download progress updates */
  onProgress?: DownloadProgressCallback;
  /** Callback for download completion */
  onComplete?: () => void;
  /** Callback for download errors */
  onError?: (error: Error) => void;
  /** Whether to show browser download dialog */
  showSaveDialog?: boolean;
  /** Custom filename for the download */
  filename?: string;
  /** Timeout in milliseconds for the download */
  timeout?: number;
}

/**
 * Enhanced file download function specifically for file manager operations.
 * Provides progress tracking, error handling, and user feedback.
 *
 * @param downloadUrl - The secure download URL from the file manager service
 * @param filename - The original filename
 * @param options - Download options including progress callbacks
 * @returns Promise<void>
 */
export const downloadFileManagerFile = async (
  downloadUrl: string,
  filename: string,
  options: FileManagerDownloadOptions = {}
): Promise<void> => {
  const {
    onProgress,
    onComplete,
    onError,
    showSaveDialog = true,
    timeout = 300000, // 5 minutes default timeout
  } = options;

  try {
    log.info('downloadFileManagerFile: Starting download', {
      filename,
      showSaveDialog,
      timeout,
    });

    // Validate inputs
    if (!downloadUrl) {
      throw new Error('Download URL is required');
    }
    if (!filename) {
      throw new Error('Filename is required');
    }

    // Create abort controller for timeout handling
    const abortController = new AbortController();
    const timeoutId = setTimeout(() => {
      abortController.abort();
    }, timeout);

    try {
      // Fetch the file with progress tracking
      const response = await fetch(downloadUrl, {
        signal: abortController.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(
          `Download failed: ${response.status} ${response.statusText}`
        );
      }

      // Get content length for progress calculation
      const contentLength = response.headers.get('content-length');
      const total = contentLength ? parseInt(contentLength, 10) : 0;

      if (!response.body) {
        throw new Error('Response body is not available');
      }

      // Create readable stream for progress tracking
      const reader = response.body.getReader();
      const chunks: Uint8Array[] = [];
      let loaded = 0;

      // Read the stream with progress updates
      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        chunks.push(value);
        loaded += value.length;

        // Call progress callback if provided
        if (onProgress && total > 0) {
          const percentage = Math.round((loaded / total) * 100);
          onProgress(loaded, total, percentage);
        }
      }

      // Combine all chunks into a single blob
      const blob = new Blob(chunks as BlobPart[]);

      // Sanitize filename for download
      const sanitizedFilename = sanitizeFileManagerFilename(filename);

      if (showSaveDialog) {
        // Use browser's save dialog
        const url = URL.createObjectURL(blob);
        try {
          const anchor = document.createElement('a');
          anchor.href = url;
          anchor.download = sanitizedFilename;
          anchor.style.display = 'none';
          document.body.appendChild(anchor);
          anchor.click();
          document.body.removeChild(anchor);
        } finally {
          URL.revokeObjectURL(url);
        }
      } else {
        // Direct download without dialog
        const url = URL.createObjectURL(blob);
        try {
          window.open(url, '_blank');
        } finally {
          URL.revokeObjectURL(url);
        }
      }

      log.info('downloadFileManagerFile: Download completed successfully', {
        filename: sanitizedFilename,
        size: loaded,
      });

      // Call completion callback
      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  } catch (error) {
    const downloadError =
      error instanceof Error ? error : new Error('Unknown download error');

    log.error('downloadFileManagerFile: Download failed', {
      filename,
      error: downloadError.message,
    });

    // Call error callback
    if (onError) {
      onError(downloadError);
    }

    throw downloadError;
  }
};

/**
 * Sanitize filename for file manager downloads.
 * More permissive than the original sanitizeFilename to preserve file extensions.
 *
 * @param filename - The original filename
 * @returns string - The sanitized filename
 */
export const sanitizeFileManagerFilename = (filename: string): string => {
  if (!filename) {
    return 'download';
  }

  // Split filename and extension
  const lastDotIndex = filename.lastIndexOf('.');
  let name = filename;
  let extension = '';

  if (lastDotIndex > 0) {
    name = filename.substring(0, lastDotIndex);
    extension = filename.substring(lastDotIndex);
  }

  // Sanitize the name part (more permissive than original)
  const sanitizedName = name
    .replace(/[<>:"|?*]/g, '_') // Remove dangerous characters
    .replace(/\s+/g, '_') // Replace spaces with underscores
    .replace(/_{2,}/g, '_') // Replace multiple underscores with single
    .replace(/^_+|_+$/g, '') // Remove leading/trailing underscores
    .slice(0, 200); // Limit length

  // Ensure we have a valid name
  const finalName = sanitizedName || 'download';

  return finalName + extension;
};

/**
 * Validate download URL format and security.
 * Ensures the URL is properly formatted and from expected sources.
 *
 * @param url - The download URL to validate
 * @returns boolean - True if URL is valid and secure
 */
export const validateDownloadUrl = (url: string): boolean => {
  try {
    const urlObj = new URL(url);

    // Check for HTTPS (security requirement)
    if (urlObj.protocol !== 'https:') {
      return false;
    }

    // Check for AWS S3 domains (expected source)
    const hostname = urlObj.hostname.toLowerCase();
    const isS3Domain =
      hostname.includes('amazonaws.com') ||
      hostname.includes('s3.') ||
      hostname.endsWith('.s3.amazonaws.com');

    if (!isS3Domain) {
      log.warn('validateDownloadUrl: URL is not from expected S3 domain', {
        hostname,
      });
      return false;
    }

    return true;
  } catch (error) {
    log.error('validateDownloadUrl: Invalid URL format', { url, error });
    return false;
  }
};

/**
 * Get estimated download time based on file size and connection speed.
 * Provides user feedback for large file downloads.
 *
 * @param fileSizeBytes - File size in bytes
 * @param connectionSpeedMbps - Estimated connection speed in Mbps (default: 10)
 * @returns object - Estimated time and formatted string
 */
export const getEstimatedDownloadTime = (
  fileSizeBytes: number,
  connectionSpeedMbps: number = 10
): { seconds: number; formatted: string } => {
  if (fileSizeBytes <= 0) {
    return { seconds: 0, formatted: 'Unknown' };
  }

  // Convert Mbps to bytes per second
  const bytesPerSecond = (connectionSpeedMbps * 1024 * 1024) / 8;

  // Calculate estimated seconds
  const seconds = Math.ceil(fileSizeBytes / bytesPerSecond);

  // Format time string
  let formatted: string;
  if (seconds < 60) {
    formatted = `${seconds} seconds`;
  } else if (seconds < 3600) {
    const minutes = Math.ceil(seconds / 60);
    formatted = `${minutes} minute${minutes > 1 ? 's' : ''}`;
  } else {
    const hours = Math.ceil(seconds / 3600);
    formatted = `${hours} hour${hours > 1 ? 's' : ''}`;
  }

  return { seconds, formatted };
};
